<template>
  <div class="container-page">
    <div
      class="container-page__head--title black--text font-weight-medium"
      :class="{ 'fs-18': breakpointXs, 'fs-25': !breakpointXs }"
    >
      <DashboardBreadcrumb :items="breadCrumbItems" />
    </div>
    <div class="container-page__mid-body mt-4">
      <v-card elevation="4" class="px-8 py-6">
        <v-row
          class="loader fill-height py-6"
          align="center"
          justify="center"
          v-if="isFreightInsuranceFormLoading || retrievingEditExportJob"
        >
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </v-row>
        <div v-else>
          <div
            class="container-page__mid-body__header--title fw-600 mb-2"
            :class="{ 'fs-18': breakpointXs, 'fs-25': !breakpointXs }"
          >
            <span>Freight Insurance and Other Charges</span>
            <span>
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn @click="makeFieldsNull" class="float-right" color="blue-grey" v-on="on">
                    <span class="font-weight-bold white--text ">Reset</span>
                    <v-icon class="font-weight-bold white--text fs-15 ml-2">fas fa-undo</v-icon>
                  </v-btn>
                </template>
                {{ resetIconMessage() }}
              </v-tooltip>
            </span>
          </div>
          <div class="container-page__mid-body__body fs-25 fw-600">
            <ValidationObserver @keydown.tab="focusNext" v-slot="{ passes }">
              <v-row>
                <v-col cols="12" :xs="12" :sm="6" :md="3" :lg="3">
                  <SelectField
                    v-model="invoiceNumber"
                    :items="exportJobInvoices"
                    :return-object="true"
                    item-text="invoiceName"
                    rules="required"
                    :loading="isFreightInsuranceFormLoading || savingExportJob"
                    label="Invoice No."
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" :xs="12" :sm="6" :md="3" :lg="3">
                  <SelectField
                    v-model="unitPriceIncludeDropdownValue"
                    :items="unitPriceIncludes"
                    item-text="text"
                    rules="required"
                    :disabled="initialDisabled"
                    :return-object="true"
                    :loading="isFreightInsuranceFormLoading || savingExportJob"
                    label="Unit Price Includes"
                  />
                </v-col>
                <v-col cols="12" :xs="12" :sm="6" :md="3" :lg="3">
                  <TextField
                    type="number"
                    v-model="exchangeRate"
                    :disabled="initialDisabled"
                    :loading="isFreightInsuranceFormLoading || savingExportJob"
                    label="Exchange Rate"
                  />
                </v-col>
                <!-- <v-col cols="12" :xs="12" :sm="6" :md="3" :lg="3">
                  <SelectField
                    v-model="showFOBIn"
                    :items="showFOBIns"
                    rules="required"
                    :disabled="initialDisabled"
                    :loading="isFreightInsuranceFormLoading || savingExportJob"
                    label="Show FOB In"
                  />
                </v-col> -->
              </v-row>
              <v-row>
                <v-col cols="6" :xs="6" :sm="3" :md="2" :lg="3">
                  <TextField
                    label="Freight Amount"
                    type="number"
                    v-model="freightAmount"
                    :disabled="initialDisabled"
                    :loading="isFreightInsuranceFormLoading || savingExportJob"
                  />
                </v-col>
                <v-col cols="6" :xs="6" :sm="3" :md="2" :lg="3">
                  <AutoComplete
                    v-model="freightCurrencyDropdownValue"
                    :return-object="true"
                    :items="currencies"
                    item-text="name"
                    :disabled="initialDisabled"
                    :loading="isFreightInsuranceFormLoading || savingExportJob"
                    label="Freight Currency"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="6" :xs="6" :sm="3" :md="2" :lg="3">
                  <TextField
                    type="number"
                    v-model="insuranceRate"
                    :disabled="initialDisabled"
                    @keyup="calculateFobPercentage"
                    @focus="insuranceAmountHandler"
                    @
                    :loading="isFreightInsuranceFormLoading || savingExportJob"
                    label="Insurance of FOB %"
                    suffix="%"
                  />
                </v-col>
                <v-col cols="6" :xs="6" :sm="3" :md="2" :lg="3">
                  <TextField
                    type="number"
                    label="Insurance Amount"
                    :disabled="initialDisabled"
                    v-model="insuranceAmount"
                    @focus="insuranceAmountHandler"
                    :loading="isFreightInsuranceFormLoading || savingExportJob"
                  />
                </v-col>
                <v-col cols="12" :xs="6" :sm="3" :md="2" :lg="3">
                  <AutoComplete
                    v-model="insuranceCurrencyDropdownValue"
                    :return-object="true"
                    :items="currencies"
                    item-text="name"
                    :disabled="initialDisabled"
                    :loading="isFreightInsuranceFormLoading || savingExportJob"
                    label="Insurance Currency"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="6" :xs="6" :sm="3" :md="2" :lg="3">
                  <TextField
                    type="number"
                    v-model="discountInFOBPercentage"
                    :disabled="initialDisabled"
                    @keyup="calculateFobPercentage"
                    @focus="discountAmountHandler"
                    :loading="isFreightInsuranceFormLoading || savingExportJob"
                    label="Discount of FOB %"
                    suffix="%"
                  />
                </v-col>
                <v-col cols="6" :xs="6" :sm="3" :md="2" :lg="3">
                  <TextField
                    type="number"
                    :disabled="initialDisabled"
                    label="Discount Amount"
                    v-model="discountAmount"
                    @focus="discountAmountHandler"
                    :loading="isFreightInsuranceFormLoading || savingExportJob"
                  />
                </v-col>
                <v-col cols="12" :xs="6" :sm="3" :md="2" :lg="3">
                  <AutoComplete
                    v-model="discountCurrencyDropdownValue"
                    :return-object="true"
                    :items="currencies"
                    item-text="name"
                    :disabled="initialDisabled"
                    :loading="isFreightInsuranceFormLoading || savingExportJob"
                    label="Discount Currency"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="6" :xs="6" :sm="3" :md="2" :lg="3">
                  <TextField
                    type="number"
                    v-model="commissionRate"
                    @keyup="calculateFobPercentage"
                    :disabled="initialDisabled"
                    @focus="commissionAmountHandler"
                    :loading="isFreightInsuranceFormLoading || savingExportJob"
                    label="Commission of FOB %"
                    suffix="%"
                  />
                </v-col>
                <v-col cols="6" :xs="6" :sm="3" :md="2" :lg="3">
                  <TextField
                    type="number"
                    label="Commission Amount"
                    v-model="commissionAmount"
                    @focus="commissionAmountHandler"
                    :disabled="initialDisabled"
                    :loading="isFreightInsuranceFormLoading || savingExportJob"
                  />
                </v-col>
                <v-col cols="12" :xs="6" :sm="3" :md="2" :lg="3">
                  <AutoComplete
                    v-model="commissionCurrencyDropdownValue"
                    :return-object="true"
                    :disabled="initialDisabled"
                    :items="currencies"
                    item-text="name"
                    :loading="isFreightInsuranceFormLoading || savingExportJob"
                    label="Commission Currency"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="6" :xs="6" :sm="3" :md="2" :lg="3">
                  <TextField
                    type="number"
                    @keyup="calculateFobPercentage"
                    v-model="otherDeductionsPercentage"
                    :disabled="initialDisabled"
                    @focus="otherDeductionsCurrencyHandler"
                    :loading="isFreightInsuranceFormLoading || savingExportJob"
                    label="Other Deductions of FOB %"
                  />
                </v-col>
                <v-col cols="6" :xs="6" :sm="3" :md="2" :lg="3">
                  <TextField
                    type="number"
                    :disabled="initialDisabled"
                    @focus="otherDeductionsCurrencyHandler"
                    label="Other Deductions Amount"
                    v-model="otherDeductionsAmount"
                    :loading="isFreightInsuranceFormLoading || savingExportJob"
                  />
                </v-col>
                <v-col cols="12" :xs="6" :sm="3" :md="2" :lg="3">
                  <AutoComplete
                    v-model="otherDeductionsCurrencyDropdownValue"
                    :return-object="true"
                    :items="currencies"
                    :disabled="initialDisabled"
                    item-text="name"
                    :loading="isFreightInsuranceFormLoading || savingExportJob"
                    label="Other Deductions Currency"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="6" :sm="6" :md="3" :lg="3">
                  <TextField
                    type="number"
                    v-model="packagingCharges"
                    value="0.00"
                    :disabled="isFobRelated"
                    :loading="isFreightInsuranceFormLoading || savingExportJob"
                    label="Packing/FOB Charges"
                  />
                </v-col>
                <v-col cols="6" :xs="6" :sm="3" :md="2" :lg="3">
                  <AutoComplete
                    label="Nature of Payment"
                    :items="natureOfPayments"
                    :disabled="isFobRelated"
                    :return-object="true"
                    item-text="text"
                    :loading="isFreightInsuranceFormLoading || savingExportJob"
                    v-model="natureOfPaymentDropdownValue"
                  />
                </v-col>
                <v-col cols="6" :xs="6" :sm="3" :md="2" :lg="3">
                  <TextField
                    type="number"
                    v-model="exporterContractNo"
                    :disabled="isFobRelated"
                    :loading="isFreightInsuranceFormLoading || savingExportJob"
                    label="Exporter Contract No"
                  />
                </v-col>
                <v-col cols="6" :xs="6" :sm="3" :md="2" :lg="3">
                  <TextField
                    type="number"
                    v-model="periodOfPaymentInDays"
                    :disabled="isFobRelated"
                    :loading="isFreightInsuranceFormLoading || savingExportJob"
                    label="Period of payment (In days)"
                  />
                </v-col>
              </v-row>
              <v-divider class="my-2"></v-divider>
              <v-row>
                <v-col cols="12" :xs="12" :sm="12" :md="12" :lg="12">
                  <v-checkbox v-model="isThirdPartyExport" label="Third Party EXPORT"></v-checkbox>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" :xs="6" :sm="6" :md="6" :lg="6">
                  <TextField
                    v-model="thirdPartyName"
                    showIcons
                    :showOnlyErrorIcons="true"
                    :disabled="!isThirdPartyExport"
                    :loading="isFreightInsuranceFormLoading || savingExportJob"
                    label="Name"
                  />
                </v-col>
                <v-col cols="12" :xs="12" :sm="6" :md="6" :lg="6">
                  <TextField
                    v-model="thirdPartyIecCode"
                    :disabled="!isThirdPartyExport"
                    showIcons
                    :showOnlyErrorIcons="true"
                    :loading="isFreightInsuranceFormLoading || savingExportJob"
                    label="IE Code"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" :xs="12" :sm="6" :md="6" :lg="6">
                  <v-textarea
                    v-model="thirdPartyAddress1"
                    filled
                    showIcons
                    :showOnlyErrorIcons="true"
                    :disabled="!isThirdPartyExport"
                    rows="5"
                    :loading="isFreightInsuranceFormLoading || savingExportJob"
                    label="Address 1"
                  ></v-textarea>
                </v-col>
                <v-col cols="12" :xs="12" :sm="6" :md="6" :lg="6">
                  <v-textarea
                    v-model="thirdPartyAddress2"
                    filled
                    showIcons
                    :showOnlyErrorIcons="true"
                    :disabled="!isThirdPartyExport"
                    rows="5"
                    :loading="isFreightInsuranceFormLoading || savingExportJob"
                    label="Address 2"
                  ></v-textarea>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" :xs="12" :sm="4" :md="4" :lg="4">
                  <TextField
                    v-model="thirdPartyCity"
                    :disabled="!isThirdPartyExport"
                    showIcons
                    :showOnlyErrorIcons="true"
                    :loading="isFreightInsuranceFormLoading || savingExportJob"
                    label="City"
                  />
                </v-col>
                <v-col cols="12" :xs="12" :sm="4" :md="4" :lg="4">
                  <AutoComplete
                    v-model="thirdPartyCountryCodeDropdownValue"
                    :return-object="true"
                    :disabled="!isThirdPartyExport"
                    :items="countries"
                    item-text="name"
                    :loading="isFreightInsuranceFormLoading || savingExportJob"
                    label="Country"
                  />
                </v-col>
                <v-col cols="12" :xs="12" :sm="4" :md="4" :lg="4">
                  <TextField
                    type="number"
                    v-model="thirdPartyPin"
                    :disabled="!isThirdPartyExport"
                    showIcons
                    :showOnlyErrorIcons="true"
                    :loading="isFreightInsuranceFormLoading || savingExportJob"
                    label="Pin"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" :xs="12" :sm="6" :md="6" :lg="6">
                  <div class="d-sm-flex justify-start" :class="{ 'text-center': breakpointXs }">
                    <v-btn
                      width="140"
                      color="info"
                      large
                      class="white--text"
                      :loading="isFreightInsuranceFormLoading || savingExportJob"
                      @click="$router.push({ name: 'dashboard.export.invoice-products' })"
                    >
                      <v-icon class="font-weight-bold fs-15 mr-2">fa fa-arrow-circle-left</v-icon>
                      <span class="font-weight-bold fs-15">Previous</span>
                    </v-btn>
                  </div>
                </v-col>
                <v-col cols="12" :xs="12" :sm="6" :md="6" :lg="6">
                  <div class="d-sm-flex justify-end" :class="{ 'text-center': breakpointXs }">
                    <v-btn
                      width="140"
                      color="button-primary"
                      large
                      class="white--text"
                      :loading="isFreightInsuranceFormLoading || savingExportJob"
                      @click="passes(saveFreightInsurance)"
                    >
                      <span class="font-weight-bold fs-15">Save</span>
                    </v-btn>
                    <div class="ml-2 mr-2 my-1"></div>
                    <v-btn
                      width="140"
                      color="info"
                      large
                      class="white--text"
                      :loading="isFreightInsuranceFormLoading || savingExportJob"
                      @click="$router.push({ name: 'dashboard.export.container' })"
                    >
                      <span class="font-weight-bold fs-15">Next</span>
                      <v-icon class="font-weight-bold fs-15 ml-2">fa fa-arrow-circle-right</v-icon>
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </ValidationObserver>
          </div>
        </div>
      </v-card>
    </div>
  </div>
</template>
<script>
import DashboardBreadcrumb from "@/modules/DashboardModule/components/DashboardBreadcrumb";
import { ValidationObserver } from "vee-validate";
import TextField from "@/components/TextField";
import SelectField from "@/components/SelectField";
import AutoComplete from "@/components/AutoComplete";
import * as MT from "@/modules/DashboardModule/modules/ImportExportModule/modules/ExportModule/store/mutationTypes";
import { SNACKBAR } from "@/store/actionTypes";
import handleExportJobMixin from "@/modules/DashboardModule/modules/ImportExportModule/mixins/handleExportJobMixin";
import commonMethodsMixin from "@/modules/commonMethodsMixin";
export default {
  name: "FreightInsurance",
  components: {
    DashboardBreadcrumb,
    ValidationObserver,
    TextField,
    SelectField,
    AutoComplete
  },
  data() {
    return {
      //   showFOBIn: "",
      isFreightInsuranceFormLoading: false,
      exchangeRate: null,
      initialDisabled: true,
      isFobRelated: true
    };
  },
  //   beforeDestroy() {
  //     this.makeFieldsNull();
  //   },
  mounted() {
    this.$store.commit(MT.SET_INVOICE_BLANK);
  },
  mixins: [commonMethodsMixin, handleExportJobMixin],
  methods: {
    setFreightCurrency(invoice) {
      this.$store.state.ExportModuleStore.invoice.freightCurrencyCodeDropdownValue =
        invoice.invoiceCurrencyCodeDropdownValue;
      this.$store.state.ExportModuleStore.invoice.freightCurrencyCode = invoice.invoiceCurrencyCode;
    },
    setInsuranceCurrency(invoice) {
      this.$store.state.ExportModuleStore.invoice.insuranceCurrencyDropdownValue =
        invoice.invoiceCurrencyCodeDropdownValue;
      this.$store.state.ExportModuleStore.invoice.insuranceCurrency = invoice.invoiceCurrencyCode;
    },
    setCommissionCurrency(invoice) {
      this.$store.state.ExportModuleStore.invoice.commissionCurrencyCodeDropdownValue =
        invoice.invoiceCurrencyCodeDropdownValue;
      this.$store.state.ExportModuleStore.invoice.commissionCurrencyCode = invoice.invoiceCurrencyCode;
    },
    setOtherDeductionsCurrency(invoice) {
      this.$store.state.ExportModuleStore.invoice.otherDeductionsCurrencyDropdownValue =
        invoice.invoiceCurrencyCodeDropdownValue;
      this.$store.state.ExportModuleStore.invoice.otherDeductionsCurrency = invoice.invoiceCurrencyCode;
    },
    getFreightCurrencyDropdownObject() {
      return {
        name: this.$store.state.ExportModuleStore.invoice.freightCurrencyCodeDropdownValue,
        code: this.$store.state.ExportModuleStore.invoice.freightCurrencyCode
      };
    },
    checkAndValidateAmount() {
      if (
        (this.insuranceRate === null || isNaN(this.insuranceRate) || this.insuranceRate <= 0) &&
        (this.insuranceAmount === null || isNaN(this.insuranceAmount) || this.insuranceAmount <= 0)
      ) {
        this.insuranceRate === null;
        this.insuranceAmount = null;
        this.insuranceCurrencyDropdownValue = this.setCurrencyNullObject();
      }
      if (
        (this.discountInFOBPercentage === null ||
          isNaN(this.discountInFOBPercentage) ||
          this.discountInFOBPercentage <= 0) &&
        (this.discountAmount === null || isNaN(this.discountAmount) || this.discountAmount <= 0)
      ) {
        this.discountInFOBPercentage;
        this.discountAmount = null;
        this.discountCurrencyDropdownValue = this.setCurrencyNullObject();
      }
      if (
        (this.commissionRate === null || isNaN(this.commissionRate) || this.commissionRate <= 0) &&
        (this.commissionAmount === null || isNaN(this.commissionAmount) || this.commissionAmount <= 0)
      ) {
        this.commissionRate = null;
        this.commissionAmount = null;
        this.commissionCurrencyDropdownValue = this.setCurrencyNullObject();
      }
      if (
        (this.otherDeductionsPercentage === null ||
          isNaN(this.otherDeductionsPercentage) ||
          this.otherDeductionsPercentage <= 0) &&
        (this.otherDeductionsAmount === null || isNaN(this.otherDeductionsAmount) || this.otherDeductionsAmount <= 0)
      ) {
        this.otherDeductionsPercentage = null;
        this.otherDeductionsAmount = null;
        this.otherDeductionsCurrencyDropdownValue = this.setCurrencyNullObject();
      }
    },
    saveFreightInsurance() {
      this.checkAndValidateAmount();
      const invoice = { ...this.$store.getters.invoice };
      this.$store.commit(MT.SET_INVOICES_IN_EXPORT_JOB, invoice);
      this.$store.dispatch(SNACKBAR, {
        color: "success",
        text: "Freight, Insurance and Other Charges saved successfully!",
        timeout: 3000
      });
      this.saveExportJob();
      return;
    },
    addContainerModalActionHandler() {
      this.showAddNewContainer = false;
    },
    discountAmountHandler() {
      const discountCurrencyDropdownValue = this.$store.state.ExportModuleStore.invoice.discountCurrencyDropdownValue;
      if (discountCurrencyDropdownValue === null) {
        this.discountCurrencyDropdownValue = this.getFreightCurrencyDropdownObject();
      }
    },
    commissionAmountHandler() {
      const commissionCurrencyCodeDropdownValue = this.$store.state.ExportModuleStore.invoice
        .commissionCurrencyCodeDropdownValue;
      if (commissionCurrencyCodeDropdownValue === null) {
        this.commissionCurrencyDropdownValue = this.getFreightCurrencyDropdownObject();
      }
    },
    insuranceAmountHandler() {
      const insuranceCurrencyCodeDropdownValue = this.$store.state.ExportModuleStore.invoice
        .insuranceCurrencyCodeDropdownValue;
      if (insuranceCurrencyCodeDropdownValue === null) {
        this.insuranceCurrencyDropdownValue = this.getFreightCurrencyDropdownObject();
      }
      this.$store.state.ExportModuleStore.invoice.insuranceAmount = this.insuranceAmount;
    },
    otherDeductionsCurrencyHandler() {
      const otherDeductionsCurrencyDropdownValue = this.$store.state.ExportModuleStore.invoice
        .otherDeductionsCurrencyDropdownValue;
      if (otherDeductionsCurrencyDropdownValue === null) {
        this.otherDeductionsCurrencyDropdownValue = this.getFreightCurrencyDropdownObject();
      }
    },
    setCurrencyNullObject() {
      return {
        name: null,
        code: null
      };
    },
    calculateFobPercentage() {
      const invoice = { ...this.$store.getters.invoice };
      if (invoice !== undefined) {
        const invoiceValue = invoice.invoiceValue !== null ? parseFloat(invoice.invoiceValue) : 0;
        // const exchangeRate = invoice.exchangeRate !== null ? parseFloat(invoice.exchangeRate) : 1;
        const freight = this.freightAmount !== null && !isNaN(this.freightAmount) ? parseFloat(this.freightAmount) : 0;
        const insurance =
          this.insuranceAmount !== null && !isNaN(this.insuranceAmount) ? parseFloat(this.insuranceAmount) : 0;
        const discount =
          this.discountAmount !== null && !isNaN(this.discountAmount) ? parseFloat(this.discountAmount) : 0;
        const commission =
          this.commissionAmount !== null && !isNaN(this.commissionAmount) ? parseFloat(this.commissionAmount) : 0;
        const insuranceRate =
          this.insuranceRate !== null && !isNaN(this.insuranceRate) ? parseFloat(this.insuranceRate) : 0;
        const discountInFOBPercentage =
          this.discountInFOBPercentage !== null && !isNaN(this.discountInFOBPercentage)
            ? parseFloat(this.discountInFOBPercentage)
            : 0;
        const commissionRate =
          this.commissionRate !== null && !isNaN(this.commissionRate) ? parseFloat(this.commissionRate) : 0;
        const otherDeductionsPercentage =
          this.otherDeductionsPercentage !== null && !isNaN(this.otherDeductionsPercentage)
            ? parseFloat(this.otherDeductionsPercentage)
            : 0;
        const invoiceFob = invoiceValue - freight - insurance - discount - commission;
        this.insuranceAmount = ((insuranceRate / 100) * invoiceFob).toFixed(2);
        this.commissionAmount = ((commissionRate / 100) * invoiceFob).toFixed(2);
        this.discountAmount = ((discountInFOBPercentage / 100) * invoiceFob).toFixed(2);
        this.otherDeductionsAmount = ((otherDeductionsPercentage / 100) * invoiceFob).toFixed(2);
      }
    },
    makeFieldsNull() {
      this.insuranceRate = null;
      this.discountInFOBPercentage = null;
      this.commissionRate = null;
      this.otherDeductionsPercentage = null;
      this.freightAmount = null;
      this.insuranceAmount = null;
      this.discountAmount = null;
      this.commissionAmount = null;
      this.otherDeductionsAmount = null;
      this.freightCurrencyDropdownValue = "";
      this.freightCurrency = null;
      this.insuranceCurrency = null;
      this.insuranceCurrencyDropdownValue = "";
      this.discountCurrency = null;
      this.discountCurrencyDropdownValue = "";
      this.commissionCurrency = null;
      this.commissionCurrencyDropdownValue = "";
      this.otherDeductionsCurrency = null;
      this.otherDeductionsCurrencyDropdownValue = "";
      this.natureOfPaymentDropdownValue = "";
      this.natureOfPayment = "";
      this.exporterContractNo = null;
      this.periodOfPaymentInDays = null;
      this.showFOBIn = "";
      this.packagingCharges = null;
      this.invoiceNumber = "";
      return;
    }
  },
  computed: {
    countries() {
      return this.$store.getters.countries;
    },
    invoiceNumber: {
      get() {
        return null;
      },
      set(invoice) {
        this.exchangeRate = invoice.exchangeRate;
        this.$store.commit(MT.SET_INVOICE_FROM_EXPORT_JOB, invoice);
        if (invoice.natureOfContract === "CIF") {
          //   this.setCommissionCurrency(invoice);
          this.setInsuranceCurrency(invoice);
          this.setFreightCurrency(invoice);
          this.initialDisabled = false;
          this.unitPriceInclude = "B";
          this.isFobRelated = false;
        } else if (invoice.natureOfContract === "C&F") {
          this.setCommissionCurrency(invoice);
          this.setFreightCurrency(invoice);
          this.unitPriceInclude = "F";
          this.initialDisabled = false;
          this.isFobRelated = false;
        } else if (invoice.natureOfContract === "C&I") {
          this.setCommissionCurrency(invoice);
          this.setInsuranceCurrency(invoice);
          this.unitPriceInclude = "I";
          this.initialDisabled = false;
          this.isFobRelated = false;
        } else if (invoice.natureOfContract === "FOB") {
          this.unitPriceInclude = "N";
          this.isFobRelated = false;
          this.initialDisabled = true;
        }
        return;
      }
    },
    exportJobInvoices() {
      return this.$store.getters.exportJob.invoices;
    },
    currencies() {
      return this.$store.getters.currencies;
    },
    // showFOBIns() {
    //   return this.$store.getters.showFOBIns;
    // },
    natureOfPayments() {
      return this.$store.getters.natureOfPayments;
    },
    unitPriceIncludes() {
      return this.$store.getters.unitPriceIncludes;
    },
    breadCrumbItems() {
      return [
        {
          title: "Dashboard",
          route: "dashboard.home",
          children: [
            {
              title: "Export",
              route: "dashboard.export",
              children: [
                {
                  title: "Freight & Insurance",
                  route: "dashboard.export.freight-insurance"
                }
              ]
            }
          ]
        }
      ];
    },
    freightAmount: {
      get() {
        return this.$store.state.ExportModuleStore.invoice.freightAmount;
      },
      set(value) {
        value = parseFloat(value);
        this.$store.commit(MT.SET_INVOICE_FREIGHT_AMOUNT, { value });
      }
    },
    freightCurrency: {
      get() {
        return this.$store.state.ExportModuleStore.invoice.freightCurrencyCode;
      },
      set(value) {
        this.$store.commit(MT.SET_INVOICE_FREIGHT_CURRENCY_CODE, { value });
      }
    },
    freightCurrencyDropdownValue: {
      get() {
        return this.$store.state.ExportModuleStore.invoice.freightCurrencyCodeDropdownValue;
      },
      set(freightCurrencyObject) {
        if ((typeof freightCurrencyObject).toLowerCase() === "string") {
          this.$store.commit(MT.SET_INVOICE_FREIGHT_CURRENCY_CODE_DROPDOWN_VALUE, { value: freightCurrencyObject });
          return;
        }
        // code reaches here means it is an object (return object)
        this.$store.commit(MT.SET_INVOICE_FREIGHT_CURRENCY_CODE_DROPDOWN_VALUE, { value: freightCurrencyObject.name });
        this.freightCurrency = freightCurrencyObject.code;
      }
    },
    insuranceRate: {
      get() {
        return this.$store.state.ExportModuleStore.invoice.insuranceRate;
      },
      set(value) {
        value = parseFloat(value);
        this.$store.commit(MT.SET_INVOICE_INSURANCE_RATE, { value });
      }
    },
    insuranceAmount: {
      get() {
        return this.$store.state.ExportModuleStore.invoice.insuranceAmount;
      },
      set(value) {
        value = parseFloat(value);
        this.$store.commit(MT.SET_INVOICE_INSURANCE_AMOUNT, { value });
      }
    },
    insuranceCurrencyDropdownValue: {
      get() {
        return this.$store.state.ExportModuleStore.invoice.insuranceCurrencyCodeDropdownValue;
      },
      set(insuranceCurrencyObject) {
        if ((typeof insuranceCurrencyObject).toLowerCase() === "string") {
          this.$store.commit(MT.SET_INVOICE_INSURANCE_CURRENCY_CODE_DROPDOWN_VALUE, {
            value: insuranceCurrencyObject
          });
          return;
        }
        // code reaches here means it is an object (return object)
        this.$store.commit(MT.SET_INVOICE_INSURANCE_CURRENCY_CODE_DROPDOWN_VALUE, {
          value: insuranceCurrencyObject.name
        });
        this.insuranceCurrency = insuranceCurrencyObject.code;
        return;
      }
    },
    insuranceCurrency: {
      get() {
        return this.$store.state.ExportModuleStore.invoice.insuranceCurrencyCode;
      },
      set(value) {
        this.$store.commit(MT.SET_INVOICE_INSURANCE_CURRENCY_CODE, { value });
      }
    },
    discountCurrencyDropdownValue: {
      get() {
        return this.$store.state.ExportModuleStore.invoice.discountCurrencyDropdownValue;
      },
      set(discountCurrencyObject) {
        if ((typeof discountCurrencyObject).toLowerCase() === "string") {
          this.$store.commit(MT.SET_INVOICE_DISCOUNT_CURRENCY_DROPDOWN_VALUE, { value: discountCurrencyObject });
          return;
        }
        // code reaches here means it is an object (return object)
        this.$store.commit(MT.SET_INVOICE_DISCOUNT_CURRENCY_DROPDOWN_VALUE, {
          value: discountCurrencyObject.name
        });
        this.discountCurrency = discountCurrencyObject.code;
        return;
      }
    },
    discountCurrency: {
      get() {
        return this.$store.state.ExportModuleStore.invoice.discountCurrency;
      },
      set(value) {
        this.$store.commit(MT.SET_INVOICE_DISCOUNT_CURRENCY, { value });
      }
    },
    discountInFOBPercentage: {
      get() {
        return this.$store.state.ExportModuleStore.invoice.discountInFOBPercentage;
      },
      set(value) {
        value = parseFloat(value);

        this.$store.commit(MT.SET_INVOICE_DISCOUNT_IN_FOB_PERCENTAGE, { value });
      }
    },
    discountAmount: {
      get() {
        return this.$store.state.ExportModuleStore.invoice.discountAmount;
      },
      set(value) {
        value = parseFloat(value);
        this.$store.commit(MT.SET_INVOICE_DISCOUNT_AMOUNT, { value });
      }
    },
    commissionRate: {
      get() {
        return this.$store.state.ExportModuleStore.invoice.commissionRate;
      },
      set(value) {
        value = parseFloat(value);
        this.$store.commit(MT.SET_INVOICE_COMMISSION_RATE, { value });
      }
    },
    commissionAmount: {
      get() {
        return this.$store.state.ExportModuleStore.invoice.commissionAmount;
      },
      set(value) {
        value = parseFloat(value);
        this.$store.commit(MT.SET_INVOICE_COMMISSION_AMOUNT, { value });
      }
    },
    commissionCurrencyDropdownValue: {
      get() {
        return this.$store.state.ExportModuleStore.invoice.commissionCurrencyCodeDropdownValue;
      },
      set(commissionCurrencyObject) {
        if ((typeof commissionCurrencyObject).toLowerCase() === "string") {
          this.$store.commit(MT.SET_INVOICE_COMMISSION_CURRENCY_CODE_DROPDOWN_VALUE, {
            value: commissionCurrencyObject
          });
          return;
        }
        // code reaches here means it is an object (return object)
        this.$store.commit(MT.SET_INVOICE_COMMISSION_CURRENCY_CODE_DROPDOWN_VALUE, {
          value: commissionCurrencyObject.name
        });
        this.commissionCurrency = commissionCurrencyObject.code;
      }
    },
    commissionCurrency: {
      get() {
        return this.$store.state.ExportModuleStore.invoice.commissionCurrencyCode;
      },
      set(value) {
        this.$store.commit(MT.SET_INVOICE_COMMISSION_CURRENCY_CODE, { value });
      }
    },
    otherDeductionsCurrencyDropdownValue: {
      get() {
        return this.$store.state.ExportModuleStore.invoice.otherDeductionsCurrencyDropdownValue;
      },
      set(otherDeductionsCurrencyObject) {
        this.$store.commit(MT.SET_INVOICE_OTHER_DEDUCTIONS_CURRENCY_DROPDOWN_VALUE, {
          value: otherDeductionsCurrencyObject.name
        });
        this.otherDeductionsCurrency = otherDeductionsCurrencyObject.code;
      }
    },
    otherDeductionsCurrency: {
      get() {
        return this.$store.state.ExportModuleStore.invoice.otherDeductionsCurrency;
      },
      set(value) {
        this.$store.commit(MT.SET_INVOICE_OTHER_DEDUCTIONS_CURRENCY, { value });
      }
    },
    otherDeductionsPercentage: {
      get() {
        return this.$store.state.ExportModuleStore.invoice.otherDeductionsPercentage;
      },
      set(value) {
        value = parseFloat(value);
        this.$store.commit(MT.SET_INVOICE_OTHER_DEDUCTIONS_PERCENTAGE, { value });
      }
    },
    otherDeductionsAmount: {
      get() {
        return this.$store.state.ExportModuleStore.invoice.otherDeductionsAmount;
      },
      set(value) {
        value = parseFloat(value);
        this.$store.commit(MT.SET_INVOICE_OTHER_DEDUCTIONS_AMOUNT, { value });
      }
    },
    unitPriceIncludeDropdownValue: {
      get() {
        const selectedunitPriceInclude = this.unitPriceInclude;
        if (selectedunitPriceInclude !== undefined && selectedunitPriceInclude !== "" && !!selectedunitPriceInclude) {
          return this.unitPriceIncludes.find(type => type.textIdentifier.includes(selectedunitPriceInclude)).text;
        } else {
          return this.unitPriceIncludes[0];
        }
      },
      set(unitPriceIncludeObject) {
        // haven't set dropdownValue in state
        if ((typeof unitPriceIncludeObject).toLowerCase() === "object") {
          this.$store.commit(MT.SET_INVOICE_UNIT_PRICE_INCLUDES, {
            value: unitPriceIncludeObject.textIdentifier
          });
          this.unitPriceInclude = unitPriceIncludeObject.textIdentifier;
        } else if ((typeof unitPriceIncludeObject).toLowerCase() === "string") {
          this.$store.commit(MT.SET_INVOICE_UNIT_PRICE_INCLUDES, unitPriceIncludeObject);
          this.unitPriceInclude = unitPriceIncludeObject;
        }
      }
    },
    unitPriceInclude: {
      get() {
        return this.$store.state.ExportModuleStore.invoice.unitPriceIncludes;
      },
      set(value) {
        this.$store.commit(MT.SET_INVOICE_UNIT_PRICE_INCLUDES, { value });
      }
    },
    packagingCharges: {
      get() {
        return this.$store.state.ExportModuleStore.invoice.packagingCharges;
      },
      set(value) {
        value = parseFloat(value);
        this.$store.commit(MT.SET_INVOICE_ADD_PACKING_CHARGES, { value });
      }
    },
    natureOfPaymentDropdownValue: {
      get() {
        const selectednatureOfPayment = this.natureOfPayment;
        if (selectednatureOfPayment !== "" && !!selectednatureOfPayment) {
          return this.natureOfPayments.find(type => type.textIdentifier.includes(selectednatureOfPayment)).text;
        } else {
          return this.natureOfPayments[0];
        }
      },
      set(natureOfPaymentObject) {
        this.$store.commit(MT.SET_INVOICE_NATURE_OF_PAYMENT, {
          value: natureOfPaymentObject.textIdentifier
        });
      }
    },
    natureOfPayment: {
      get() {
        return this.$store.state.ExportModuleStore.invoice.natureOfPayment;
      },
      set(value) {
        this.$store.commit(MT.SET_INVOICE_NATURE_OF_PAYMENT, { value });
      }
    },
    exporterContractNo: {
      get() {
        return this.$store.state.ExportModuleStore.invoice.exporterContractNo;
      },
      set(value) {
        this.$store.commit(MT.SET_INVOICE_EXPORTER_CONTRACT_NO, { value });
      }
    },
    periodOfPaymentInDays: {
      get() {
        return this.$store.state.ExportModuleStore.invoice.periodOfPaymentInDays;
      },
      set(value) {
        this.$store.commit(MT.SET_INVOICE_PERIOD_OF_PAYMENT_IN_DAYS, { value });
      }
    },
    isThirdPartyExport: {
      get() {
        const isThirdParty = this.$store.state.ExportModuleStore.invoice.isThirdParty;
        if (isThirdParty === null || isThirdParty === "N") {
          return false;
        }
        return true;
      },
      set(selection) {
        selection = selection ? "Y" : "N";
        this.$store.commit(MT.SET_INVOICE_IS_THIRD_PARTY, { value: selection });
        return;
      }
    },

    thirdPartyName: {
      get() {
        return this.$store.state.ExportModuleStore.invoice.thirdPartyName;
      },
      set(value) {
        this.$store.commit(MT.SET_INVOICE_THIRD_PARTY_NAME, { value });
      }
    },
    thirdPartyIecCode: {
      get() {
        return this.$store.state.ExportModuleStore.invoice.thirdPartyIec;
      },
      set(value) {
        this.$store.commit(MT.SET_INVOICE_THIRD_PARTY_IEC, { value });
      }
    },
    thirdPartyAddress1: {
      get() {
        return this.$store.state.ExportModuleStore.invoice.thirdPartyAddress1;
      },
      set(value) {
        this.$store.commit(MT.SET_INVOICE_THIRD_PARTY_ADDRESS1, { value });
      }
    },
    thirdPartyAddress2: {
      get() {
        return this.$store.state.ExportModuleStore.invoice.thirdPartyAddress2;
      },
      set(value) {
        this.$store.commit(MT.SET_INVOICE_THIRD_PARTY_ADDRESS2, { value });
      }
    },
    thirdPartyCity: {
      get() {
        return this.$store.state.ExportModuleStore.invoice.thirdPartyCity;
      },
      set(value) {
        this.$store.commit(MT.SET_INVOICE_THIRD_PARTY_CITY, { value });
      }
    },
    thirdPartyCountryCodeDropdownValue: {
      get() {
        return this.$store.state.ExportModuleStore.invoice.thirdPartyCountryCodeDropdownValue;
      },
      set(thirdPartyCountryCodeObject) {
        this.$store.commit(MT.SET_INVOICE_THIRD_PARTY_COUNTRY_CODE_DROPDOWN_VALUE, {
          value: thirdPartyCountryCodeObject.name
        });
        this.thirdPartyCountryCode = thirdPartyCountryCodeObject.code;
      }
    },
    thirdPartyCountryCode: {
      get() {
        return this.$store.state.ExportModuleStore.invoice.thirdPartyCountryCode;
      },
      set(value) {
        this.$store.commit(MT.SET_INVOICE_THIRD_PARTY_COUNTRY_CODE, { value });
      }
    },
    thirdPartyPin: {
      get() {
        return this.$store.state.ExportModuleStore.invoice.thirdPartyPin;
      },
      set(value) {
        this.$store.commit(MT.SET_INVOICE_THIRD_PARTY_PIN, { value });
      }
    }
  }
};
</script>
<style lang="scss">
.container-page {
  &__mid-body {
    &__table {
      .v-data-table {
        background: transparent !important;
      }
    }
  }
}
</style>
